import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft, Calendar, MapPin, Globe, Clock, ChevronRight, Loader2, Info,
} from 'lucide-react';
import { eventService } from '../services/dataService';
import { useApp } from '../context/AppContext';
import {
  getEventModuleLabel,
  isEventModuleEnabled,
  type ConferenceEvent,
} from '@conference/shared';

function fmtDate(d: string): string {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  live:      { bg: 'rgba(34,197,94,0.15)', text: '#22C55E' },
  published: { bg: 'rgba(56,189,248,0.15)', text: '#38BDF8' },
  upcoming:  { bg: 'rgba(56,189,248,0.15)', text: '#38BDF8' },
  completed: { bg: 'rgba(148,163,184,0.15)', text: '#94A3B8' },
  draft:     { bg: 'rgba(251,191,36,0.15)', text: '#F59E0B' },
};

export default function EventDetailScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setCurrentEventId } = useApp();
  const [event, setEvent] = useState<ConferenceEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) { setNotFound(true); setLoading(false); return; }
    eventService.getById(id)
      .then((data) => {
        if (!data) setNotFound(true);
        else {
          const selected = data as unknown as ConferenceEvent;
          setEvent(selected);
          setCurrentEventId(id, selected);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, setCurrentEventId]);

  if (loading) {
    return (
      <div className="size-full flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-4" style={{ background: 'var(--app-bg)' }}>
        <Info className="w-12 h-12" style={{ color: 'var(--app-text-subtle)' }} />
        <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Event not found</p>
        <button onClick={() => navigate(-1)} className="text-sm" style={{ color: 'var(--app-accent)' }}>Go back</button>
      </div>
    );
  }

  const statusStyle = STATUS_COLORS[event.status.toLowerCase()] ?? STATUS_COLORS.draft;
  const agendaEnabled = isEventModuleEnabled(event.app_modules, 'agenda');
  const agendaLabel = getEventModuleLabel(event.app_modules, 'agenda');
  const sameYear = event.date_start.slice(0, 4) === event.date_end.slice(0, 4);
  const dateRange = sameYear
    ? `${new Date(event.date_start + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – ${fmtDate(event.date_end)}`
    : `${fmtDate(event.date_start)} – ${fmtDate(event.date_end)}`;

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Cover */}
      <div className="relative h-48 shrink-0">
        {event.image_url ? (
          <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: 'var(--app-accent-dim)' }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), var(--app-bg))' }} />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 size-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-90 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        {/* Status badge */}
        <span
          className="absolute top-4 right-4 text-xs px-3 py-1 rounded-full capitalize backdrop-blur-sm"
          style={{ background: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.text}40` }}
        >
          {event.status}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-4 pb-8">
          <h1 className="text-[var(--app-text)] text-2xl mb-4">{event.name}</h1>

          {/* Info rows */}
          <div className="flex flex-col gap-3 mb-5">
            <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--app-text-muted)' }}>
              <Calendar className="w-4 h-4 shrink-0" style={{ color: 'var(--app-accent)' }} />
              <span>{dateRange}</span>
            </div>
            <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--app-text-muted)' }}>
              <MapPin className="w-4 h-4 shrink-0" style={{ color: 'var(--app-accent)' }} />
              <span>{[event.venue, event.location].filter(Boolean).join(', ')}</span>
            </div>
            <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--app-text-muted)' }}>
              <Clock className="w-4 h-4 shrink-0" style={{ color: 'var(--app-accent)' }} />
              <span>Timezone: {event.timezone}</span>
            </div>
            {event.max_attendees && (
              <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--app-text-muted)' }}>
                <Info className="w-4 h-4 shrink-0" style={{ color: 'var(--app-accent)' }} />
                <span>Up to {event.max_attendees.toLocaleString()} attendees</span>
              </div>
            )}
            {event.website && (
              <button
                onClick={() => window.open(event.website!.startsWith('http') ? event.website! : `https://${event.website}`, '_blank')}
                className="flex items-center gap-3 text-sm text-left active:opacity-70"
                style={{ color: 'var(--app-text-muted)' }}
              >
                <Globe className="w-4 h-4 shrink-0" style={{ color: 'var(--app-accent)' }} />
                <span className="underline">{event.website}</span>
              </button>
            )}
          </div>

          {/* Description */}
          {event.description && (
            <div className="mb-6">
              <h2 className="text-[var(--app-text)] text-base mb-2">About</h2>
              <p className="text-[var(--app-text-muted)] text-sm leading-relaxed">{event.description}</p>
            </div>
          )}

          {/* CTA */}
          {agendaEnabled && (
          <button
            onClick={() => navigate('/agenda')}
            className="h-14 rounded-xl text-white text-base w-full flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            style={{ background: 'var(--app-accent-solid)' }}
          >
            View Full {agendaLabel}
            <ChevronRight className="w-5 h-5" />
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
